import { useState } from 'react';
import { Button, TextInput } from '@c2fo/liquidity';
import { EyeOpenIcon, EyeCrossedIcon } from '@c2fo/liquidity/icons';
import { createServiceAccount } from '../../api/ServiceAccounts';
import type { ServiceAccount } from '../../api/ServiceAccounts/types';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import { Modal } from '../Modal';
import { FormField } from '../FormField';

interface NewServiceAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (account: ServiceAccount) => void;
  onError: (message: string) => void;
}

export const NewServiceAccountModal: React.FC<NewServiceAccountModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  onError,
}) => {
  const apiContext = useApiContext();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [description, setDescription] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isFormValid = () => {
    return displayName.trim() && username.trim() && password.length >= 8 && password === confirmPassword;
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      onError('Display name is required');
      return;
    }

    if (!username.trim()) {
      onError('Username is required');
      return;
    }

    if (password.length < 8) {
      onError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      onError('Passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      const account = await createServiceAccount(
        { ...apiContext, serviceAccount: 'epadmin' },
        {
          serviceAccount: {
            name: '',
            displayName: displayName.trim(),
            username: username.trim(),
            description: description.trim() || undefined,
            enabled: true,
          },
          password,
        },
      );

      onCreated(account);
      handleClose();
    } catch {
      onError('Failed to create service account');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisplayName('');
    setUsername('');
    setDescription('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Service Account"
      maxWidth="md"
      footer={
        <>
          <Button variant="tertiary" className="rounded-md" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="rounded-md text-white"
            onClick={handleSave}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Display Name" required>
          <TextInput
            value={displayName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
            placeholder="Enter a display name"
            className="w-full"
            size="sm"
          />
        </FormField>

        <FormField label="Username" required>
          <TextInput
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
            placeholder="Enter a username"
            className="w-full font-mono"
            size="sm"
          />
        </FormField>

        <FormField label="Description">
          <TextInput
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            placeholder="Enter an optional description"
            className="w-full"
            size="sm"
          />
        </FormField>

        <FormField label="Password" required>
          <div className="relative">
            <TextInput
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              placeholder="Enter password (min 8 characters)"
              className="w-full pr-10"
              size="sm"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? (
                <EyeOpenIcon className="h-5 w-5 stroke-2" />
              ) : (
                <EyeCrossedIcon className="h-5 w-5 stroke-2" />
              )}
            </button>
          </div>
        </FormField>

        <FormField
          label="Confirm Password"
          required
          error={password && confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
        >
          <div className="relative">
            <TextInput
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="w-full pr-10"
              size="sm"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showConfirmPassword ? (
                <EyeOpenIcon className="h-5 w-5 stroke-2" />
              ) : (
                <EyeCrossedIcon className="h-5 w-5 stroke-2" />
              )}
            </button>
          </div>
        </FormField>
      </div>
    </Modal>
  );
};
